// frontend/src/components/SystemMonitoring.tsx
// Раздел «📊 Мониторинг системы» — спидометры + контейнеры + БД + бэкапы
// Только для superadmin. Live-обновление каждые 5 сек.

import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';

// ─────────────────────────────────────────────
//  Типы
// ─────────────────────────────────────────────
interface Overview {
  server: { hostname: string; platform: string; osRelease: string; nodeVersion: string; uptime: number; systemUptime: number };
  cpu: { usage: number; cores: number; model: string; loadAvg: number[] };
  memory: { total: number; used: number; free: number; usage: number };
  disk: { total: number; used: number; free: number; usage: number };
}

interface Container {
  name: string;
  cpu: number;
  memUsage: string;
  memPerc: number;
  status: string;
  isHrprod: boolean;
  isHrdev: boolean;
}

interface ContainersResponse {
  total: number;
  prod: number;
  dev: number;
  containers: Container[];
}

interface DatabaseResponse {
  mongo: { connected: boolean; version?: string; database?: string; collections?: number; documents?: number; dataSize?: number; storageSize?: number; indexes?: number; error?: string };
  redis: { connected: boolean; keys?: number; memory?: string; error?: string };
}

interface BackupsResponse {
  count: number;
  totalSize: number;
  latest: { name: string; size: number; mtime: number; ageHours: number } | null;
  oldest: { name: string; size: number; mtime: number; ageHours: number } | null;
  error?: string;
}

interface ApiStats {
  requests: { lastHour: number; last24h: number };
  errors: { lastHour: number; last24h: number };
  avgResponseMs: number;
  systemErrors: { unresolved: number; last24h: number };
  uptimeSince: number;
}

interface HistoryPoint {
  ts: number;
  cpu: number;
  mem: number;
  disk: number;
  requests: number;
  errors: number;
}

// ─────────────────────────────────────────────
//  Утилиты
// ─────────────────────────────────────────────
const fmtBytes = (b: number): string => {
  if (!b) return '0 B';
  if (b > 1024 ** 3) return (b / 1024 ** 3).toFixed(1) + ' GB';
  if (b > 1024 ** 2) return (b / 1024 ** 2).toFixed(1) + ' MB';
  if (b > 1024) return (b / 1024).toFixed(1) + ' KB';
  return b + ' B';
};

const fmtUptime = (sec: number): string => {
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (d > 0) return `${d}д ${h}ч`;
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м`;
};

const statusColor = (pct: number): string => {
  if (pct >= 85) return '#ef4444';
  if (pct >= 60) return '#f59e0b';
  return '#10b981';
};

// ─────────────────────────────────────────────
//  Speedometer (SVG, полкруг)
// ─────────────────────────────────────────────
const Speedometer: React.FC<{ value: number; label: string; unit?: string; sub?: string }> = ({ value, label, unit = '%', sub }) => {
  const size = 160;
  const cx = size / 2;
  const cy = size * 0.62;
  const r = 60;
  const color = statusColor(value);

  // Полукруг: от 180° до 0° (слева направо)
  const startAngle = 180;
  const endAngle = 0;
  const valueAngle = 180 - (value / 100) * 180;

  const polar = (angle: number, radius: number) => {
    const rad = (angle * Math.PI) / 180;
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) };
  };

  const arc = (startA: number, endA: number, radius: number, strokeWidth: number, stroke: string) => {
    const s = polar(startA, radius);
    const e = polar(endA, radius);
    const largeArc = Math.abs(endA - startA) > 180 ? 1 : 0;
    const sweep = startA > endA ? 1 : 0;
    return <path d={`M ${s.x} ${s.y} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${e.x} ${e.y}`} fill="none" stroke={stroke} strokeWidth={strokeWidth} strokeLinecap="round" />;
  };

  const needle = polar(valueAngle, r - 8);
  const needleBase = polar(valueAngle + 90, 4);
  const needleBase2 = polar(valueAngle - 90, 4);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '12px' }}>
      <svg width={size} height={size * 0.72} viewBox={`0 0 ${size} ${size * 0.72}`}>
        {/* Фоновая дуга */}
        {arc(startAngle, endAngle, r, 12, '#e2e8f0')}
        {/* Значение */}
        {value > 0 && arc(startAngle, valueAngle, r, 12, color)}
        {/* Стрелка */}
        <polygon
          points={`${needle.x},${needle.y} ${needleBase.x},${needleBase.y} ${needleBase2.x},${needleBase2.y}`}
          fill="#1e293b"
        />
        <circle cx={cx} cy={cy} r={6} fill="#1e293b" />
        <circle cx={cx} cy={cy} r={2} fill="#fff" />
      </svg>
      <div style={{ textAlign: 'center', marginTop: '-10px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color, lineHeight: 1 }}>
          {value}<span style={{ fontSize: '16px', color: '#64748b' }}>{unit}</span>
        </div>
        <div style={{ fontSize: '13px', color: '#475569', marginTop: '4px', fontWeight: 600 }}>{label}</div>
        {sub && <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{sub}</div>}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
//  Sparkline (SVG line)
// ─────────────────────────────────────────────
const Sparkline: React.FC<{ points: HistoryPoint[]; field: 'cpu' | 'mem' | 'disk'; color: string; label: string }> = ({ points, field, color, label }) => {
  if (points.length < 2) {
    return (
      <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '10px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
        {label}: накапливается…
      </div>
    );
  }
  const w = 260, h = 50, pad = 4;
  const values = points.map(p => p[field]);
  const max = Math.max(100, ...values);
  const step = (w - pad * 2) / (points.length - 1);
  const path = points.map((p, i) => {
    const x = pad + i * step;
    const y = h - pad - (p[field] / max) * (h - pad * 2);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  return (
    <div style={{ padding: '10px', background: '#f8fafc', borderRadius: '10px' }}>
      <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px', fontWeight: 600 }}>{label}</div>
      <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {/* Последняя точка */}
        {points.length > 0 && (() => {
          const last = points[points.length - 1];
          const x = pad + (points.length - 1) * step;
          const y = h - pad - (last[field] / max) * (h - pad * 2);
          return <circle cx={x} cy={y} r={3} fill={color} />;
        })()}
      </svg>
    </div>
  );
};

// ─────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────
const SystemMonitoring: React.FC = () => {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [containers, setContainers] = useState<ContainersResponse | null>(null);
  const [database, setDatabase] = useState<DatabaseResponse | null>(null);
  const [backups, setBackups] = useState<BackupsResponse | null>(null);
  const [apiStats, setApiStats] = useState<ApiStats | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const fetchAll = useCallback(async () => {
    try {
      const [ov, ct, db, bk, ap, hs] = await Promise.allSettled([
        api.get('/admin/monitoring/overview'),
        api.get('/admin/monitoring/containers'),
        api.get('/admin/monitoring/database'),
        api.get('/admin/monitoring/backups'),
        api.get('/admin/monitoring/api-stats'),
        api.get('/admin/monitoring/history'),
      ]);
      if (ov.status === 'fulfilled') setOverview(ov.value.data);
      if (ct.status === 'fulfilled') setContainers(ct.value.data);
      if (db.status === 'fulfilled') setDatabase(db.value.data);
      if (bk.status === 'fulfilled') setBackups(bk.value.data);
      if (ap.status === 'fulfilled') setApiStats(ap.value.data);
      if (hs.status === 'fulfilled') setHistory(hs.value.data.points || []);
      setError(null);
      setLastUpdate(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(fetchAll, 5000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  if (loading && !overview) {
    return <div style={{ padding: '60px', textAlign: 'center', color: '#64748b' }}>Загрузка метрик системы…</div>;
  }

  if (error && !overview) {
    return <div style={{ padding: '40px', background: 'rgba(239,68,68,0.08)', color: '#dc2626', borderRadius: '12px' }}>
      ❌ {error}
    </div>;
  }

  const card = (title: string, icon: string, content: React.ReactNode, extra?: React.ReactNode) => (
    <div style={{
      background: '#fff',
      borderRadius: '14px',
      padding: '18px',
      boxShadow: '0 1px 3px rgba(15,23,42,0.06), 0 1px 2px rgba(15,23,42,0.04)',
      border: '1px solid #e2e8f0',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
          <span style={{ marginRight: '6px' }}>{icon}</span>{title}
        </h3>
        {extra}
      </div>
      {content}
    </div>
  );

  const row = (label: string, value: React.ReactNode) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
      <span style={{ color: '#64748b' }}>{label}</span>
      <span style={{ color: '#0f172a', fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div style={{ padding: '0 4px' }}>
      {/* ─── Header ─── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 800, color: '#0f172a' }}>
            📊 Мониторинг системы
          </h2>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#64748b' }}>
            Live-обновление каждые 5 секунд
            {lastUpdate && <> · Последнее: {lastUpdate.toLocaleTimeString('ru-RU')}</>}
          </p>
        </div>
        <button
          onClick={fetchAll}
          style={{
            padding: '8px 16px',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #6a11cb, #2575fc)',
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          🔄 Обновить
        </button>
      </div>

      {/* ─── Speedometers ─── */}
      {overview && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          marginBottom: '16px',
          background: '#fff',
          borderRadius: '14px',
          padding: '16px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
          border: '1px solid #e2e8f0',
        }}>
          <Speedometer
            value={overview.cpu.usage}
            label="CPU"
            sub={`${overview.cpu.cores} ядра · load ${overview.cpu.loadAvg[0].toFixed(2)}`}
          />
          <Speedometer
            value={overview.memory.usage}
            label="RAM"
            sub={`${fmtBytes(overview.memory.used)} / ${fmtBytes(overview.memory.total)}`}
          />
          <Speedometer
            value={overview.disk.usage}
            label="Диск"
            sub={`${fmtBytes(overview.disk.used)} / ${fmtBytes(overview.disk.total)}`}
          />
        </div>
      )}

      {/* ─── Server + API ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        {overview && card('Сервер', '🖥️', <>
          {row('Hostname', overview.server.hostname)}
          {row('Платформа', `${overview.server.platform} · ${overview.server.osRelease}`)}
          {row('Node.js', overview.server.nodeVersion)}
          {row('Uptime (backend)', fmtUptime(overview.server.uptime))}
          {row('Uptime (система)', fmtUptime(overview.server.systemUptime))}
          {row('CPU', overview.cpu.model)}
        </>)}

        {apiStats && card('API-статистика', '📡', <>
          {row('Запросов за час', apiStats.requests.lastHour)}
          {row('Запросов за 24ч', apiStats.requests.last24h)}
          {row('Ошибок за час', <span style={{ color: apiStats.errors.lastHour > 0 ? '#ef4444' : '#0f172a' }}>{apiStats.errors.lastHour}</span>)}
          {row('Ошибок за 24ч', <span style={{ color: apiStats.errors.last24h > 0 ? '#f59e0b' : '#0f172a' }}>{apiStats.errors.last24h}</span>)}
          {row('Среднее время', `${apiStats.avgResponseMs} мс`)}
          {row('System errors', <span style={{ color: apiStats.systemErrors.unresolved > 0 ? '#ef4444' : '#10b981' }}>
            {apiStats.systemErrors.unresolved} (24ч: {apiStats.systemErrors.last24h})
          </span>)}
        </>)}
      </div>

      {/* ─── History (sparklines) ─── */}
      {history.length > 0 && (
        <div style={{
          background: '#fff',
          borderRadius: '14px',
          padding: '16px',
          marginBottom: '16px',
          boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
          border: '1px solid #e2e8f0',
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>
            📈 История (24 часа) · точек: {history.length}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
            <Sparkline points={history} field="cpu" color="#6a11cb" label="CPU" />
            <Sparkline points={history} field="mem" color="#2575fc" label="RAM" />
            <Sparkline points={history} field="disk" color="#059669" label="Диск" />
          </div>
        </div>
      )}

      {/* ─── Containers ─── */}
      {containers && card('Контейнеры', '🐳',
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
          {containers.containers.map((c) => (
            <div key={c.name} style={{
              padding: '12px',
              borderRadius: '10px',
              background: c.isHrprod ? 'linear-gradient(135deg, rgba(16,185,129,0.08), rgba(5,150,105,0.02))' : '#f8fafc',
              border: `1px solid ${c.isHrprod ? 'rgba(16,185,129,0.3)' : '#e2e8f0'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                <div style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                  {c.isHrprod ? '🟢' : c.isHrdev ? '🔵' : '⚪'} {c.name}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '11px', color: '#64748b' }}>
                <span>CPU: <b style={{ color: statusColor(c.cpu * 10) }}>{c.cpu}%</b></span>
                <span>RAM: <b style={{ color: statusColor(c.memPerc) }}>{c.memUsage}</b> ({c.memPerc}%)</span>
              </div>
            </div>
          ))}
        </div>,
        <span style={{ fontSize: '12px', color: '#64748b' }}>
          всего: {containers.total} · prod: {containers.prod} · dev: {containers.dev}
        </span>
      )}

      {/* ─── Database + Backups ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '16px' }}>
        {database && card('База данных', '🗄️', <>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#6a11cb', marginTop: '4px', marginBottom: '6px' }}>MongoDB</div>
          {database.mongo.connected ? <>
            {row('Версия', database.mongo.version || '?')}
            {row('База', database.mongo.database || '?')}
            {row('Коллекций', database.mongo.collections || 0)}
            {row('Документов', (database.mongo.documents || 0).toLocaleString('ru-RU'))}
            {row('Размер данных', fmtBytes(database.mongo.dataSize || 0))}
            {row('Индексов', database.mongo.indexes || 0)}
          </> : <div style={{ color: '#ef4444', fontSize: '12px' }}>❌ Не подключен: {database.mongo.error}</div>}

          <div style={{ fontSize: '12px', fontWeight: 700, color: '#dc2626', marginTop: '12px', marginBottom: '6px' }}>Redis</div>
          {database.redis.connected ? <>
            {row('Ключей', database.redis.keys || 0)}
            {row('Память', database.redis.memory || '?')}
          </> : <div style={{ color: '#f59e0b', fontSize: '12px' }}>⚠️ {database.redis.error || 'не подключен'}</div>}
        </>)}

        {backups && !backups.error && card('Бэкапы', '💾', <>
          {row('Всего', `${backups.count} шт`)}
          {row('Общий размер', fmtBytes(backups.totalSize))}
          {backups.latest && <>
            {row('Последний', <span style={{ color: backups.latest.ageHours > 36 ? '#ef4444' : '#10b981' }}>
              {backups.latest.ageHours}ч назад
            </span>)}
            {row('Размер последнего', fmtBytes(backups.latest.size))}
          </>}
          {backups.oldest && row('Самый старый', `${backups.oldest.ageHours}ч назад`)}
        </>)}

        {backups?.error && card('Бэкапы', '💾',
          <div style={{ color: '#ef4444', fontSize: '12px' }}>❌ {backups.error}</div>
        )}
      </div>

      {/* ─── Footer ─── */}
      <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '11px', color: '#94a3b8' }}>
        Данные обновляются автоматически · Только для superadmin
      </div>
    </div>
  );
};

export default SystemMonitoring;
